/**
 * TeamSystem Impact - Theme Store
 * Zustand store per gestione dark mode con persistenza localStorage
 */

import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  resolvedTheme: () => 'light' | 'dark';
}

const STORAGE_KEY = 'teamsystem-impact-theme';

const getInitialMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // ignore
  }
  return 'light';
};

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const syncDarkClass = (resolved: 'light' | 'dark') => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }
};

export const useThemeStore = create<ThemeState>()((set, get) => {
  const initialMode = getInitialMode();
  const initialResolved = initialMode === 'system' ? getSystemTheme() : initialMode;
  syncDarkClass(initialResolved);

  return {
    mode: initialMode,

    setMode: (mode) => {
      set({ mode });
      localStorage.setItem(STORAGE_KEY, mode);
      const resolved = mode === 'system' ? getSystemTheme() : mode;
      syncDarkClass(resolved);
    },

    resolvedTheme: () => {
      const { mode } = get();
      return mode === 'system' ? getSystemTheme() : mode;
    },
  };
});
